"use client"

import React, { useState } from 'react'
import { useRouter } from 'next/navigation'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { Upload, Send, Loader2, FileText } from 'lucide-react'
import { useAuthContext } from '@/lib/context/auth-context'
import { useCreateReportIssue } from '@/hooks/use-report-issue'
import {
  reportIssueSchema,
  ReportIssueFormValues,
} from '@/lib/validations/report-issue.validation'
import { ResponseDialog } from '@/components/ui/response-dialog'

export default function SupportForm({ className }: { className?: string }) {
  const router = useRouter()
  const { isAuthenticated } = useAuthContext()
  const createReportIssueMutation = useCreateReportIssue()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [issueId, setIssueId] = useState<string>('')

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<ReportIssueFormValues>({
    resolver: zodResolver(reportIssueSchema),
    defaultValues: {
      issue_description: '',
      attachment: null,
    },
  })

  const attachment = watch('attachment')

  const onSubmit = (data: ReportIssueFormValues) => {
    // Check if user is authenticated
    if (!isAuthenticated) {
      // Store the intended destination (support page)
      sessionStorage.setItem('redirectAfterLogin', '/support')
      // Redirect to login
      router.push('/login')
      return
    }

    // User is authenticated, submit the issue
    createReportIssueMutation.mutate(
      {
        issue_description: data.issue_description,
        attachment: data.attachment || null,
      },
      {
        onSuccess: (response) => {
          // Store issue ID and show success dialog
          setIssueId(response.data.issue_id)
          setDialogOpen(true)
          // Reset form after successful submission
          reset()
        },
      }
    )
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    setValue('attachment', file, { shouldValidate: true })
  }

  return (
    <Card className={cn('border-border/60 shadow-subtle', className)}>
      <CardHeader>
        <CardTitle>Contact Support</CardTitle>
        <CardDescription>
          {isAuthenticated 
            ? 'Describe the issue you are experiencing and attach any relevant files'
            : 'Sign in to report an issue or contact support'
          }
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Issue Description */}
          <div className='space-y-2'>
            <Label htmlFor='issue_description'>
              Issue Description <span className='text-destructive'>*</span>
            </Label>
            <div className='relative'>
              <FileText className='absolute left-3 top-3 h-4 w-4 text-muted-foreground' />
              <Textarea
                id='issue_description'
                placeholder='Describe the issue in detail...'
                className='min-h-[150px] pl-10'
                {...register('issue_description')}
              />
            </div>
            {errors.issue_description && (
              <p className='text-sm text-destructive'>
                {errors.issue_description.message}
              </p>
            )}
          </div>

          {/* Attachment */}
          <div className='space-y-2'>
            <Label htmlFor='attachment'>
              Attachment (JPG, PNG, PDF - Max 10MB)
            </Label>
            <div className='flex items-center gap-3'>
              <Button type='button' variant='outline' asChild>
                <label className='cursor-pointer flex items-center gap-2'>
                  <Upload className='h-4 w-4' />
                  <span>Choose File</span>
                  <input
                    id='attachment'
                    type='file'
                    accept='image/png,image/jpeg,application/pdf'
                    className='hidden'
                    onChange={handleFileChange}
                  />
                </label>
              </Button>
              <span className='text-sm text-muted-foreground truncate'>
                {attachment ? attachment.name : 'No file selected'}
              </span>
            </div>
            {errors.attachment && (
              <p className='text-sm text-destructive'>
                {errors.attachment.message}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              {isAuthenticated 
                ? 'Avg. response time: under 12 hours'
                : 'Please sign in to submit'
              }
            </span>
            <Button
              type='submit'
              disabled={createReportIssueMutation.isPending}
            >
              {createReportIssueMutation.isPending ? (
                <>
                  <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                  Submitting...
                </>
              ) : isAuthenticated ? (
                <>
                  <Send className='h-4 w-4 mr-2' />
                  Submit Issue
                </>
              ) : (
                <>
                  <Send className='h-4 w-4 mr-2' />
                  Sign in to Submit
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>

      {/* Success Dialog */}
      <ResponseDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        variant='success'
        title='Issue Reported Successfully!'
        description='Thank you for reporting this issue. Our support team has been notified and will get back to you soon. Please keep your Issue ID for reference.'
        copyText={issueId}
        copyLabel='Issue ID'
        primaryButton={{
          label: 'Done',
          onClick: () => setDialogOpen(false),
        }}
      />
    </Card>
  )
}
